import {
  ActionIcon,
  Anchor,
  Button,
  Group,
  Loader,
  Text,
  Tooltip,
  UnstyledButton,
} from '@mantine/core';
import { ReactNode } from 'react';
import {
  IconCheck,
  IconChevronRight,
  IconCircleCheck,
  IconExclamationCircle,
  IconTrash,
} from '@tabler/icons-react';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';

type StatusBarProps =
  | { variant: 'extracting' }
  | { variant: 'error'; onRetry?: () => void; isRetrying?: boolean }
  | {
      variant: 'ready';
      contactName: string;
      isProcessing?: boolean;
      onProcess: () => void;
      onDismiss?: () => void;
      isDismissing?: boolean;
    }
  | { variant: 'done'; contactName: string }
  | { variant: 'fileTo'; rootFolderLabel: string; onFileTo: () => void }
  | { variant: 'filed'; folderName: string; count: number; onOpen: () => void };

export default function StatusBar(props: StatusBarProps) {
  switch (props.variant) {
    case 'extracting':
      return <ExtractingBar />;
    case 'error':
      return <ErrorBar onRetry={props.onRetry} isRetrying={props.isRetrying} />;
    case 'ready':
      return <ReadyBar {...props} />;
    case 'done':
      return <DoneBar contactName={props.contactName} />;
    case 'fileTo':
      return <FileToBar rootFolderLabel={props.rootFolderLabel} onFileTo={props.onFileTo} />;
    case 'filed':
      return <FiledBar folderName={props.folderName} count={props.count} onOpen={props.onOpen} />;
  }
}

function FileToBar({
  rootFolderLabel,
  onFileTo,
}: {
  rootFolderLabel: string;
  onFileTo: () => void;
}) {
  return (
    <BarContainer bg="gray.0" testId="status-bar-file-to">
      <IconCircleCheck size={24} color="var(--mantine-color-gray-5)" />
      <Text size="sm" fw={FONT_WEIGHTS.medium} c="gray.10" style={{ flex: 1 }}>
        Completed.{' '}
        <Anchor
          component="button"
          underline="never"
          size="sm"
          fw={FONT_WEIGHTS.medium}
          c="brand.7"
          onClick={onFileTo}
          data-testid="file-to-link"
        >
          File to &quot;{rootFolderLabel}&quot;
        </Anchor>{' '}
        to keep data organised
      </Text>
    </BarContainer>
  );
}

function FiledBar({
  folderName,
  count,
  onOpen,
}: {
  folderName: string;
  count: number;
  onOpen: () => void;
}) {
  return (
    <UnstyledButton onClick={onOpen} w="100%" data-testid="status-bar-filed">
      <BarContainer bg="gray.0">
        <IconCircleCheck size={24} color="var(--mantine-color-gray-5)" />
        <Text size="sm" fw={FONT_WEIGHTS.medium} c="gray.10" style={{ flex: 1 }}>
          Filed to &quot;{folderName}&quot; ({count})
        </Text>
        <IconChevronRight size={20} color="var(--mantine-color-gray-8)" />
      </BarContainer>
    </UnstyledButton>
  );
}

function ExtractingBar() {
  return (
    <BarContainer bg="gray.0" testId="status-bar-extracting">
      <Loader size={24} color="brand.6" />
      <Text size="sm" fw={FONT_WEIGHTS.medium} c="gray.5" style={{ flex: 1 }}>
        Extracting data. Feel free to leave, we&apos;ll notify once it is done...
      </Text>
    </BarContainer>
  );
}

function ErrorBar({ onRetry, isRetrying }: { onRetry?: () => void; isRetrying?: boolean }) {
  return (
    <BarContainer bg="error.1" testId="status-bar-error">
      <IconExclamationCircle size={24} color="var(--mantine-color-error-7)" />
      <Text size="sm" fw={FONT_WEIGHTS.medium} c="gray.10" style={{ flex: 1 }}>
        Something went wrong, please{' '}
        {onRetry ? (
          <RetryLink onRetry={onRetry} isRetrying={isRetrying} />
        ) : (
          <PlaceholderRetryLink />
        )}
      </Text>
    </BarContainer>
  );
}

function ReadyBar({
  contactName,
  isProcessing,
  onProcess,
  onDismiss,
  isDismissing,
}: Extract<StatusBarProps, { variant: 'ready' }>) {
  return (
    <BarContainer bg="brand.0" testId="status-bar-ready">
      <Text size="sm" fw={FONT_WEIGHTS.medium} c="gray.10" style={{ flex: 1 }}>
        {contactName}
      </Text>
      <Group gap="xxs" wrap="nowrap">
        {onDismiss && (
          <ActionIcon
            variant="subtle"
            color="brand.7"
            size={24}
            radius={8}
            aria-label={`Remove ${contactName}`}
            data-testid="dismiss-contact-button"
            loading={isDismissing}
            disabled={isDismissing}
            onClick={onDismiss}
          >
            <IconTrash size={16} />
          </ActionIcon>
        )}
        <Button
          color="brand.6"
          h={24}
          px="xxs"
          radius={8}
          fz="xs"
          fw={FONT_WEIGHTS.semibold}
          loading={isProcessing}
          loaderProps={{ size: 14, color: 'white' }}
          onClick={onProcess}
          data-testid="process-data-button"
        >
          Process data
        </Button>
      </Group>
    </BarContainer>
  );
}

function DoneBar({ contactName }: { contactName: string }) {
  return (
    <BarContainer bg="gray.0" testId="status-bar-done">
      <IconCheck size={24} color="var(--mantine-color-gray-5)" />
      <Text size="sm" fw={FONT_WEIGHTS.medium} c="gray.5" style={{ flex: 1 }}>
        {contactName}
      </Text>
    </BarContainer>
  );
}

function BarContainer({
  bg,
  testId,
  children,
}: {
  bg: string;
  testId?: string;
  children: ReactNode;
}) {
  return (
    <Group
      bg={bg}
      px="sm"
      py="xs"
      gap="xxs"
      w="100%"
      wrap="nowrap"
      style={{ borderRadius: 20 }}
      data-testid={testId}
    >
      {children}
    </Group>
  );
}

function RetryLink({ onRetry, isRetrying }: { onRetry: () => void; isRetrying?: boolean }) {
  return (
    <Anchor
      component="button"
      type="button"
      underline="never"
      size="sm"
      fw={FONT_WEIGHTS.medium}
      c="brand.7"
      disabled={isRetrying}
      onClick={onRetry}
      data-testid="status-bar-error-retry"
    >
      {isRetrying ? 'retrying…' : 'try again'}
    </Anchor>
  );
}

function PlaceholderRetryLink() {
  return (
    <Tooltip label="Not implemented yet" withArrow>
      <Anchor
        component="button"
        underline="never"
        size="sm"
        fw={FONT_WEIGHTS.medium}
        c="brand.7"
        data-testid="status-bar-error-retry"
      >
        try again
      </Anchor>
    </Tooltip>
  );
}
